import React, { useState } from 'react';
import { CalendarClock, FileText, RotateCcw, Play, ChevronDown } from 'lucide-react';
import { api, ApiError } from '../../../lib/api';
import NewInvoiceDialog from '../../../components/admin/NewInvoiceDialog';
import { renewalInvoicePrefill } from '../../../lib/maintenance';

// The school's maintenance term, on its Financial tab. Reads the `renewal`
// block the financial summary returns: the current term with a state, the
// history, and website projects that have no term yet ("Start term").

const STATE = {
  active:     { label: 'Active',      tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  in_renewal: { label: 'In renewal',  tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  ended:      { label: 'Ended',       tone: 'bg-red-500/10 text-red-300 border-red-500/20' },
  lapsed:     { label: 'Lapsed',      tone: 'bg-red-500/10 text-red-300 border-red-500/20' },
  declined:   { label: 'Declined',    tone: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
};
const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.valueOf()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
function daysLine(t) {
  if (t.state === 'declined') return `The school declined to renew${t.decidedAt ? ` on ${fmt(t.decidedAt)}` : ''}.`;
  if (t.daysLeft < 0) return `Ended ${-t.daysLeft} day${t.daysLeft === -1 ? '' : 's'} ago${t.state === 'lapsed' ? ' with no decision — contact the school' : ''}.`;
  if (t.daysLeft === 0) return 'Ends today.';
  return `${t.daysLeft} day${t.daysLeft === 1 ? '' : 's'} left${t.state === 'in_renewal' ? ' — renewal window' : ''}.`;
}

export default function RenewalPanel({ account, renewal, onChange }) {
  const [draftOpen, setDraftOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [startFor, setStartFor] = useState(null); // project row → start-term form
  const [startDate, setStartDate] = useState('');

  if (!renewal) return null;
  const t = renewal.current;
  const candidates = renewal.projectsWithoutTerm || [];
  if (!t && candidates.length === 0) return null;

  const decide = async (decision) => {
    if (busy) return;
    if (decision === 'declined' && !window.confirm(`Record that ${account.name} is not renewing? It leaves the renewal queue (history is kept).`)) return;
    setBusy(true); setError('');
    try { await api.post(`/api/admin/service-terms/${t.id}/decision`, { decision }); await onChange?.(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Could not record the decision'); }
    finally { setBusy(false); }
  };
  const startTerm = async (e) => {
    e.preventDefault();
    if (busy || !startFor) return;
    setBusy(true); setError('');
    try {
      await api.post('/api/admin/service-terms', { projectId: startFor.id, startsOn: startDate || null });
      setStartFor(null);
      await onChange?.();
    } catch (err) { setError(err instanceof ApiError ? err.message : 'Could not start the term'); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="renewal-panel" aria-labelledby="renewal-title">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-2">
        <CalendarClock className="w-3 h-3" aria-hidden="true" /> <span id="renewal-title">Maintenance term</span>
      </div>

      {t ? (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14px] text-white font-medium">Year {t.termNumber}{t.kind === 'included' ? ' · included with the build' : ' · paid'}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border uppercase tracking-wider ${STATE[t.state]?.tone || STATE.active.tone}`} data-testid="renewal-state">{STATE[t.state]?.label || t.state}</span>
            </div>
            <div className="text-[12px] text-gray-400 mt-1">{fmt(t.startsOn)} → {fmt(t.endsOn)} · {daysLine(t)}</div>
            {t.notes && <div className="text-[12px] text-gray-500 mt-1">{t.notes}</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
            {t.state !== 'declined' && (
              <button type="button" onClick={() => setDraftOpen(true)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/40 text-[12px] text-white hover:bg-[#B0FC38]/25" data-testid="renewal-draft">
                <FileText className="w-3 h-3" aria-hidden="true" /> Draft renewal invoice
              </button>
            )}
            {t.state === 'declined' ? (
              <button type="button" onClick={() => decide('pending')} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/[0.1] text-[12px] text-gray-400 hover:text-white disabled:opacity-50" data-testid="renewal-reopen">
                <RotateCcw className="w-3 h-3" aria-hidden="true" /> Reopen
              </button>
            ) : (
              <button type="button" onClick={() => decide('declined')} disabled={busy} title="The school is not renewing — leaves the queue, history kept" className="px-2.5 py-1.5 rounded-full border border-white/[0.1] text-[12px] text-gray-400 hover:text-red-300 hover:border-red-500/30 disabled:opacity-50" data-testid="renewal-decline">Declined</button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-gray-500">No maintenance term on record yet.</p>
      )}

      {candidates.length > 0 && (
        <div className={`${t ? 'mt-3 pt-3 border-t border-white/[0.05]' : 'mt-2'}`} data-testid="renewal-start">
          {candidates.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 flex-wrap py-1">
              <div className="text-[12px] text-gray-400">
                <span className="text-gray-200">{p.name}</span> is {p.status} with no term.
                {p.suggestedStart ? ` Year 1 would start ${fmt(p.suggestedStart)} (${p.suggestedFrom === 'contract' ? 'contract signed' : 'go-live'}).` : ' No contract or go-live date on file.'}
              </div>
              {startFor?.id === p.id ? (
                <form onSubmit={startTerm} className="flex items-center gap-2">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required={!p.suggestedStart} aria-label="Term start date" className="px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-white" />
                  <button type="submit" disabled={busy} className="px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-medium disabled:opacity-50" data-testid="renewal-start-save">Start</button>
                  <button type="button" onClick={() => setStartFor(null)} className="text-[12px] text-gray-500 hover:text-white">Cancel</button>
                </form>
              ) : (
                <button type="button" onClick={() => { setStartFor(p); setStartDate(p.suggestedStart || ''); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/[0.1] text-[12px] text-gray-300 hover:text-white" data-testid={`renewal-start-${p.id}`}>
                  <Play className="w-3 h-3" aria-hidden="true" /> Start term
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {renewal.history?.length > 1 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowHistory((v) => !v)} className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300" aria-expanded={showHistory}>
            <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? 'rotate-180' : ''}`} aria-hidden="true" /> {showHistory ? 'Hide' : 'Show'} earlier terms ({renewal.history.length - 1})
          </button>
          {showHistory && (
            <ul className="mt-1.5 text-[12px] text-gray-500 space-y-0.5 list-none m-0 p-0">
              {renewal.history.slice(1).map((h) => <li key={h.id}>Year {h.termNumber} · {h.kind} · {fmt(h.startsOn)} → {fmt(h.endsOn)} · {h.decision}</li>)}
            </ul>
          )}
        </div>
      )}

      {error && <div role="alert" className="mt-2 text-[12px] text-red-300">{error}</div>}

      {t && (
        <NewInvoiceDialog
          open={draftOpen}
          defaultAccountLocked
          onClose={() => setDraftOpen(false)}
          prefill={renewalInvoicePrefill({ account, term: t, amountCents: renewal.defaultAmountCents })}
          onCreated={async () => { setDraftOpen(false); await onChange?.(); }}
        />
      )}
    </section>
  );
}
